import React, { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import { oneOf } from 'prop-types';

import { Rating, Image, Heading } from '@thd-olt-component-react/core-ui';

import { brands } from '../../../data/paint-modals.json';
import sharedStyles from '../paint-modal-content.module.scss';
import styles from './Brand.module.scss';

const cx = classNames.bind({ ...styles, ...sharedStyles });

export const Brand = ({ brandKey, type }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('brands.ready'); }, []);

  const brandData = brands[brandKey][type];
  const dataClassnames = cx('table__data', 'table__data--brand');

  const PERFORMANCE_LABEL = 'PERFORMANCE';
  const COVERAGE_LABEL = 'COVERAGE';
  const DURABILITY_LABEL = 'DURABILITY';
  const EASY_TO_CLEAN_LABEL = 'EASY TO CLEAN';
  const STAIN_BLOCKING = 'STAIN BLOCKING';
  const VOC_LABEL = 'Odor & VOC Conteent';

  return (
    <div className={cx('brand-modal', 'paint-modal-content')}>
      <Heading
        underline
        className={cx('brand-modal__header')}
        title={`Brand Options (${ type })`}
      />
      <p className={cx('brand-modal__description')}>
        Our top paint brands side by side. Compare performance,
        durability, value, and more.
      </p>

      <div className={cx('table')}>
        <div className={cx('table__attributes')}>
          <div className={cx('table__row')}>{PERFORMANCE_LABEL}</div>
          <div className={cx('table__row')}>{COVERAGE_LABEL}</div>
          <div className={cx('table__row', 'table__row--rating-label')}>{DURABILITY_LABEL}</div>
          <div className={cx('table__row', 'table__row--rating-label')}>{EASY_TO_CLEAN_LABEL}</div>
          <div className={cx('table__row', 'table__row--rating-label')}>{STAIN_BLOCKING}</div>
          <div className={cx('table__row')}>{VOC_LABEL}</div>
        </div>
        <div className={cx('table__body')}>
          <div className={cx('table__row')}>
            { brandData.map((brand) => {
              const {
                image,
                name
              } = brand;
              return (
                <Image
                  className={cx('brand__image')}
                  src={image}
                  alt={name}
                  key={name}
                  width={100}
                  height={50}
                />
              );
            }) }
          </div>
          <div className={cx('table__row', 'table__row--shaded')}>
            { brandData.map((brand) => {
              const { name, performance } = brand;
              return (
                <div className={dataClassnames} key={`${name}-performance`}>{ performance }</div>
              );
            }) }
          </div>
          <div className={cx('table__row')}>
            { brandData.map((brand) => {
              const { name, coverage } = brand;
              return (
                <div className={dataClassnames} key={`${name}-coverage`}>{ coverage }</div>
              );
            }) }
          </div>
          <div className={cx('table__row', 'table__row--shaded', 'table__row--rating')}>
            { brandData.map((brand) => {
              const { name, durability } = brand;
              return (
                <div className={dataClassnames}>
                  <Rating
                    className={cx('brand__rating')}
                    key={`${name}-durability`}
                    value={durability}
                    type="dash"
                    orange
                  >
                    { durability }
                  </Rating>
                </div>
              );
            }) }
          </div>
          <div className={cx('table__row', 'table__row--shaded', 'table__row--rating')}>
            { brandData.map((brand) => {
              const { name, washability } = brand;
              return (
                <div className={dataClassnames}>
                  <Rating
                    className={cx('brand__rating')}
                    key={`${name}-washability`}
                    value={washability}
                    type="dash"
                    orange
                  >
                    { washability }
                  </Rating>
                </div>
              );
            }) }
          </div>
          <div className={cx('table__row', 'table__row--shaded', 'table__row--rating')}>
            { brandData.map((brand) => {
              const { name, stainBlocking } = brand;
              return (
                <div className={dataClassnames}>
                  <Rating
                    className={cx('brand__rating')}
                    key={`${name}-stainBlocking`}
                    value={stainBlocking}
                    type="dash"
                    orange
                  >
                    { stainBlocking }
                  </Rating>
                </div>
              );
            }) }
          </div>
          <div className={cx('table__row')}>
            { brandData.map((brand) => {
              const { name, odorVOCContent } = brand;
              return (
                <div className={dataClassnames} key={`${name}-odorVOCContent`}>{ odorVOCContent }</div>
              );
            }) }
          </div>
        </div>
      </div>
      <div className={cx('brand-modal__disclaimer')}>
        * Colorants added to this base may increase voc level significantly depending on color
        choices
      </div>
    </div>
  );
};

Brand.propTypes = {
  brandKey: oneOf(['Behr', 'PPG - Glidden']),
  type: oneOf((['Interior Paint', 'Exterior Paint']))
};

Brand.defaultProps = {
  brandKey: 'Behr',
  type: 'Interior Paint',
};

Brand.displayName = 'PaintModalContent.Brand';
